import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone, TableWrap } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day as dayText, money, parseMoney, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Bars, downloadCsv, Kpi, plain, Table } from './Reports.tsx';

/**
 * Fleet: the business's vehicles. Managers see every vehicle with its alerts and costs, give
 * vehicles to drivers, plan services and keep the papers; drivers see the vehicles given to them
 * and write the odometer, the fuel and the problems they notice.
 */

type VState = 'active' | 'workshop' | 'retired';
type Kind = 'car' | 'van' | 'truck' | 'motorbike' | 'other';
type Fuel = 'diesel' | 'petrol' | 'lpg' | 'hybrid' | 'electric';
type SKind = 'service' | 'repair' | 'tyres' | 'inspection' | 'cleaning' | 'other';
type SState = 'reported' | 'planned' | 'done' | 'cancelled';
type PKind = 'insurance' | 'inspection' | 'road_tax' | 'registration' | 'leasing' | 'other';
type Tab = 'overview' | 'vehicles' | 'costs';

const KINDS: Kind[] = ['car', 'van', 'truck', 'motorbike', 'other'];
const FUELS: Fuel[] = ['diesel', 'petrol', 'lpg', 'hybrid', 'electric'];
const S_KINDS: SKind[] = ['service', 'repair', 'tyres', 'inspection', 'cleaning', 'other'];
const P_KINDS: PKind[] = ['insurance', 'inspection', 'road_tax', 'registration', 'leasing', 'other'];
const KIND_ICON: Record<Kind, IconName> = { car: 'car', van: 'truck', truck: 'truck', motorbike: 'bike', other: 'car' };
const STATE: Record<VState, { tone: Tone; icon: IconName }> = {
  active: { tone: 'ok', icon: 'checkCircle' },
  workshop: { tone: 'warn', icon: 'settings' },
  retired: { tone: 'neutral', icon: 'archive' },
};
const S_STATE: Record<SState, { tone: Tone; icon: IconName }> = {
  reported: { tone: 'danger', icon: 'alert' },
  planned: { tone: 'info', icon: 'calendar' },
  done: { tone: 'ok', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
};

interface Alert {
  kind: 'paper' | 'service' | 'reported';
  level: 'late' | 'soon';
  id: string;
  vehicle: string;
  plate: string;
  what: string;
  day: string | null;
  km: number | null;
}
interface Overview {
  today: string;
  vehicles: { active: number; workshop: number; free: number };
  alerts: Alert[];
  costs: { year: number; month: number; fuel: number; service: number; paper: number; months: { month: string; fuel: number; service: number; paper: number }[] } | null;
  can: { view: boolean; manage: boolean };
}
interface VehicleRow {
  id: string;
  plate: string;
  make: string;
  model: string;
  year: number | null;
  kind: Kind;
  fuel: Fuel;
  vin: string | null;
  color: string | null;
  seats: number | null;
  location: string | null;
  location_name: string | null;
  driver: string | null;
  driver_name: string | null;
  state: VState;
  retired_reason: string | null;
  acquired: string | null;
  odometer: number;
  note: string | null;
  cost_year: number | null;
  alerts: { late: number; soon: number };
}
interface Service {
  id: string;
  kind: SKind;
  state: SState;
  description: string;
  due_day: string | null;
  due_km: number | null;
  day: string | null;
  odometer: number | null;
  vendor: string | null;
  amount: number | null;
  reported_by_name: string | null;
  created_at: string;
}
interface Paper {
  id: string;
  kind: PKind;
  reference: string | null;
  provider: string | null;
  start: string | null;
  end: string | null;
  amount: number;
  note: string | null;
}
interface Detail extends Omit<VehicleRow, 'cost_year' | 'alerts'> {
  drivers: { id: string; driver: string; name: string; start: string; end: string | null }[];
  readings: { id: string; day: string; value: number; source: 'reading' | 'fuel' | 'service' }[];
  fills: { id: string; day: string; liters: number; amount: number | null; odometer: number | null; full: boolean; station: string | null }[];
  services: Service[];
  papers: Paper[];
  alerts: Alert[];
  consumption: number | null;
  costs: { kind: 'fuel' | 'service' | 'paper'; month: string; amount: number }[] | null;
  can: { view: boolean; manage: boolean };
}

const km = (n: number) => `${new Intl.NumberFormat(locale()).format(n)} km`;
const monthLabel = (m: string) => new Intl.DateTimeFormat(locale(), { month: 'short', timeZone: 'UTC' }).format(new Date(`${m}-01T00:00:00Z`));
const alertText = (a: Alert) =>
  a.kind === 'paper'
    ? t(a.level === 'late' ? 'fleet.al_paper_late' : 'fleet.al_paper_soon', { paper: t(`fleet.paper_${a.what}` as MessageKey), day: dayText(a.day!) })
    : a.kind === 'reported'
      ? t('fleet.al_reported', { what: a.what })
      : t(a.level === 'late' ? 'fleet.al_service_late' : 'fleet.al_service_soon', {
          what: a.what,
          when: [a.day ? dayText(a.day) : null, a.km !== null ? km(a.km) : null].filter(Boolean).join(' · '),
        });

export function Plate({ plate, small }: { plate: string; small?: boolean }) {
  return (
    <span className={`fl-plate${small ? ' small' : ''}`}>
      <span className="fl-plate-band" aria-hidden="true">
        AL
      </span>
      <span className="fl-plate-text">{plate}</span>
    </span>
  );
}

export function Fleet() {
  const [params, setParams] = useSearchParams();
  const { data: o, reload } = useAction<Overview>('fleet.overview');
  const [version, setVersion] = useState(0);
  const [adding, setAdding] = useState(false);
  const open = params.get('vehicle');
  const view = !!o?.can.view;
  const tabs: { key: Tab; icon: IconName }[] = view
    ? [
        { key: 'overview', icon: 'chart' },
        { key: 'vehicles', icon: 'truck' },
        { key: 'costs', icon: 'cash' },
      ]
    : [];
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : view ? 'overview' : 'vehicles') as Tab;
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  const late = o?.alerts.filter((a) => a.level === 'late').length ?? 0;
  return (
    <Shell>
      <div className="page reports fl-page">
        <div className="page-head">
          <div>
            <h1>{t('fleet.title')}</h1>
            <p className="muted">{view ? t('fleet.subtitle') : t('fleet.subtitle_driver')}</p>
          </div>
          {o?.can.manage ? (
            <div className="page-actions">
              <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
                {t('fleet.add_vehicle')}
              </Button>
            </div>
          ) : null}
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : o.costs ? (
          <section className="rep-kpis" aria-label={t('fleet.at_a_glance')}>
            <Kpi label={t('fleet.k_in_use')} value={String(o.vehicles.active)} hint={t('fleet.k_in_use_hint', { workshop: o.vehicles.workshop, free: o.vehicles.free })} />
            <Kpi label={t('fleet.k_alerts')} value={String(o.alerts.length)} hint={late ? tn('fleet.k_alerts_late', late) : t('fleet.k_alerts_ok')} warn={late > 0} />
            <Kpi label={t('fleet.k_month')} value={money(o.costs.month)} hint={t('fleet.k_month_hint')} />
            <Kpi label={t('fleet.k_year')} value={money(o.costs.year)} hint={t('fleet.k_year_hint', { fuel: money(o.costs.fuel), service: money(o.costs.service), paper: money(o.costs.paper) })} />
          </section>
        ) : null}
        {tabs.length ? (
          <div className="segmented tabs" role="tablist" aria-label={t('fleet.sections')}>
            {tabs.map((x) => (
              <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'overview' ? null : x.key })}>
                <Icon name={x.icon} size={16} />
                {t(`fleet.tab_${x.key}` as MessageKey)}
                {x.key === 'overview' && late ? <span className="hr-tab-badge">{late}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
        {!o ? null : tab === 'overview' ? (
          <OverviewTab o={o} onOpen={(id) => set({ vehicle: id })} />
        ) : tab === 'vehicles' ? (
          <VehiclesTab key={version} manage={o.can.manage} onOpen={(id) => set({ vehicle: id })} onAdd={() => setAdding(true)} />
        ) : (
          <CostsTab key={version} today={o.today} onOpen={(id) => set({ vehicle: id })} />
        )}
      </div>
      {open ? <VehicleDialog id={open} onClose={() => set({ vehicle: null })} onChange={changed} /> : null}
      {adding ? (
        <VehicleForm
          initial={null}
          onClose={() => setAdding(false)}
          onSaved={async (id) => {
            setAdding(false);
            await changed();
            set({ vehicle: id });
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- overview ---------------------------------------------------------------------------

function AlertList({ alerts, onOpen }: { alerts: Alert[]; onOpen?: (vehicle: string) => void }) {
  return (
    <ul className="fl-alerts">
      {alerts.map((a) => (
        <li key={`${a.kind}-${a.id}`}>
          <button type="button" className={`fl-alert ${a.level}`} onClick={onOpen ? () => onOpen(a.vehicle) : undefined} disabled={!onOpen}>
            <Icon name={a.kind === 'paper' ? 'file' : a.kind === 'reported' ? 'alert' : 'settings'} size={18} />
            {onOpen ? <Plate plate={a.plate} small /> : null}
            <span className="fl-alert-text">{alertText(a)}</span>
            <StatusBadge
              tone={a.level === 'late' ? 'danger' : 'warn'}
              icon={a.level === 'late' ? 'alert' : 'clock'}
              label={t(a.level === 'late' ? 'fleet.late' : a.kind === 'reported' ? 'fleet.to_plan' : 'fleet.soon')}
            />
          </button>
        </li>
      ))}
    </ul>
  );
}

function OverviewTab({ o, onOpen }: { o: Overview; onOpen: (id: string) => void }) {
  const c = o.costs!;
  return (
    <div className="fl-overview">
      <section className="panel">
        <div className="panel-head">
          <h2>{t('fleet.alerts')}</h2>
          <span className="muted small">{t('fleet.alerts_hint')}</span>
        </div>
        {o.alerts.length ? <AlertList alerts={o.alerts} onOpen={onOpen} /> : <EmptyState icon="checkCircle" title={t('fleet.no_alerts')} body={t('fleet.no_alerts_body')} />}
      </section>
      <section className="panel">
        <div className="panel-head">
          <h2>{t('fleet.costs_this_year')}</h2>
          <strong>{money(c.year)}</strong>
        </div>
        {c.year ? (
          <>
            <Bars
              items={c.months.map((m) => ({
                key: m.month,
                label: monthLabel(m.month),
                value: m.fuel + m.service + m.paper,
                tip: t('fleet.month_tip', { month: monthLabel(m.month), fuel: money(m.fuel), service: money(m.service), paper: money(m.paper) }),
              }))}
            />
            <ul className="fl-split">
              {(['fuel', 'service', 'paper'] as const).map((k) => (
                <li key={k}>
                  <span className={`fl-dot fl-${k}-dot`} aria-hidden="true" />
                  <span>{t(`fleet.cost_${k}` as MessageKey)}</span>
                  <strong>{money(c[k])}</strong>
                  <span className="muted small">{c.year ? `${Math.round((c[k] / c.year) * 100)} %` : ''}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="muted">{t('fleet.no_costs')}</p>
        )}
      </section>
    </div>
  );
}

// ---- vehicles ---------------------------------------------------------------------------

function VehiclesTab({ manage, onOpen, onAdd }: { manage: boolean; onOpen: (id: string) => void; onAdd: () => void }) {
  const [state, setState] = useState<'all' | VState>('all');
  const [search, setSearch] = useState('');
  const { data } = useAction<{ vehicles: VehicleRow[]; can: { view: boolean } }>('fleet.vehicles', { state, search: search.trim() || null });
  return (
    <section className="panel">
      <div className="fl-tools">
        <input className="input fl-search" type="search" placeholder={t('fleet.search')} aria-label={t('fleet.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
        {data?.can.view ? (
          <div className="segmented small" role="tablist" aria-label={t('fleet.state')}>
            {(['all', 'active', 'workshop', 'retired'] as const).map((s) => (
              <button key={s} role="tab" aria-selected={state === s} className={state === s ? 'active' : ''} onClick={() => setState(s)}>
                {t(`fleet.filter_${s}` as MessageKey)}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={220} r="var(--radius-lg)" />
      ) : !data.vehicles.length ? (
        <EmptyState
          icon="truck"
          title={search || state !== 'all' ? t('fleet.none_found') : data.can.view ? t('fleet.no_vehicles') : t('fleet.no_vehicles_driver')}
          body={search || state !== 'all' ? undefined : data.can.view ? t('fleet.no_vehicles_body') : t('fleet.no_vehicles_driver_body')}
          action={
            manage && !search && state === 'all' ? (
              <Button variant="primary" icon="plus" onClick={onAdd}>
                {t('fleet.add_vehicle')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="fl-cards">
          {data.vehicles.map((v) => (
            <li key={v.id}>
              <button type="button" className={`fl-card${v.state === 'retired' ? ' retired' : ''}`} onClick={() => onOpen(v.id)}>
                <span className="fl-card-top">
                  <Plate plate={v.plate} />
                  {v.state !== 'active' ? <StatusBadge tone={STATE[v.state].tone} icon={STATE[v.state].icon} label={t(`fleet.state_${v.state}` as MessageKey)} /> : null}
                </span>
                <span className="fl-card-name">
                  <Icon name={KIND_ICON[v.kind]} size={16} />
                  <strong>
                    {v.make} {v.model}
                  </strong>
                  {v.year ? <span className="muted small">{v.year}</span> : null}
                </span>
                <span className="fl-card-meta small">
                  <span>
                    <Icon name="contact" size={14} /> {v.driver_name ?? t('fleet.no_driver')}
                  </span>
                  <span>
                    <Icon name="trendUp" size={14} /> {km(v.odometer)}
                  </span>
                  <span>
                    <Icon name="flame" size={14} /> {t(`fleet.fuel_${v.fuel}` as MessageKey)}
                  </span>
                </span>
                {data.can.view ? (
                  <span className="fl-card-foot small">
                    {v.alerts.late ? (
                      <StatusBadge tone="danger" icon="alert" label={tn('fleet.n_late', v.alerts.late)} />
                    ) : v.alerts.soon ? (
                      <StatusBadge tone="warn" icon="clock" label={tn('fleet.n_soon', v.alerts.soon)} />
                    ) : v.state !== 'retired' ? (
                      <StatusBadge tone="ok" icon="checkCircle" label={t('fleet.all_in_order')} />
                    ) : (
                      <span className="muted">{v.retired_reason}</span>
                    )}
                    {v.cost_year !== null ? <span className="muted">{t('fleet.cost_year', { amount: money(v.cost_year) })}</span> : null}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---- costs ------------------------------------------------------------------------------

function CostsTab({ today, onOpen }: { today: string; onOpen: (id: string) => void }) {
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const { data } = useAction<{
    year: number;
    vehicles: { id: string; plate: string; make: string; model: string; fuel: number; service: number; paper: number; total: number; km: number | null; per_km: number | null }[];
    total: number;
  }>('fleet.costs', { year });
  const head = [t('fleet.vehicle'), t('fleet.cost_fuel'), t('fleet.cost_service'), t('fleet.cost_paper'), t('fleet.total'), t('fleet.km_driven'), t('fleet.per_km')];
  return (
    <section className="panel">
      <div className="fl-tools">
        <div className="fl-year">
          <IconButton icon="back" label={t('fleet.prev_year')} onClick={() => setYear((y) => y - 1)} />
          <strong>{year}</strong>
          <IconButton icon="chevronRight" label={t('fleet.next_year')} onClick={() => setYear((y) => y + 1)} disabled={year >= thisYear} />
        </div>
        <span className="muted small fl-tools-text">{t('fleet.costs_hint')}</span>
        <Button
          size="sm"
          icon="download"
          disabled={!data?.vehicles.length}
          onClick={() =>
            data &&
            downloadCsv(
              `fleet-costs-${year}.csv`,
              head,
              data.vehicles.map((v) => [`${v.plate} ${v.make} ${v.model}`, plain(v.fuel), plain(v.service), plain(v.paper), plain(v.total), v.km ?? '', v.per_km !== null ? plain(v.per_km) : '']),
            )
          }
        >
          {t('fleet.csv')}
        </Button>
      </div>
      {!data ? (
        <Skeleton h={200} r="var(--radius-lg)" />
      ) : (
        <TableWrap>
          <Table
            head={head}
            phoneHide={[1, 2, 3, 5, 6]}
            empty={t('fleet.no_costs')}
            rows={data.vehicles.map((v) => ({
              key: v.id,
              onClick: () => onOpen(v.id),
              cells: [
                <span className="fl-cost-name" key="n">
                  <Plate plate={v.plate} small />
                  <span className="muted small">
                    {v.make} {v.model}
                  </span>
                </span>,
                money(v.fuel),
                money(v.service),
                money(v.paper),
                <strong key="t">{money(v.total)}</strong>,
                v.km !== null ? km(v.km) : '—',
                v.per_km !== null ? money(v.per_km) : '—',
              ],
            }))}
            foot={data.vehicles.length ? [t('fleet.total'), '', '', '', money(data.total), '', ''] : null}
          />
        </TableWrap>
      )}
    </section>
  );
}

// ---- one vehicle ------------------------------------------------------------------------

type DTab = 'about' | 'fuel' | 'services' | 'papers' | 'drivers' | 'odometer';
type Form =
  | { kind: 'edit' }
  | { kind: 'driver' }
  | { kind: 'fuel' }
  | { kind: 'odometer' }
  | { kind: 'issue' }
  | { kind: 'service'; service: Service | null }
  | { kind: 'finish'; service: Service }
  | { kind: 'paper'; paper: Paper | null }
  | { kind: 'retire' }
  | { kind: 'drop-paper'; paper: Paper }
  | { kind: 'drop-fuel'; id: string }
  | { kind: 'cancel-service'; service: Service };

function VehicleDialog({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => Promise<void> }) {
  const { data: v, reload } = useAction<Detail>('fleet.vehicle', { id });
  const { busy, run } = useSave();
  const [tab, setTab] = useState<DTab>('about');
  const [form, setForm] = useState<Form | null>(null);
  const done = async () => {
    setForm(null);
    await reload();
    await onChange();
  };
  if (!v)
    return (
      <Dialog title={t('fleet.vehicle')} onClose={onClose} size="xl">
        <Skeleton h={320} r="var(--radius-lg)" />
      </Dialog>
    );
  const manage = v.can.manage;
  const live = v.state !== 'retired';
  const tabs: { key: DTab; icon: IconName; n?: number; show: boolean }[] = [
    { key: 'about', icon: 'eye', show: true },
    { key: 'fuel', icon: 'flame', n: v.fills.length, show: true },
    { key: 'services', icon: 'settings', n: v.services.filter((s) => s.state === 'reported' || s.state === 'planned').length, show: true },
    { key: 'papers', icon: 'file', n: v.papers.length, show: v.can.view },
    { key: 'drivers', icon: 'contact', show: v.can.view },
    { key: 'odometer', icon: 'trendUp', show: true },
  ];
  const cost12 = v.costs?.reduce((s, c) => s + c.amount, 0) ?? null;
  return (
    <Dialog title={`${v.plate} · ${v.make} ${v.model}`} onClose={onClose} size="xl">
      <div className="fl-head">
        <Plate plate={v.plate} />
        <div className="fl-head-text">
          <strong>
            {v.make} {v.model} {v.year ? <span className="muted">· {v.year}</span> : null}
          </strong>
          <span className="muted small">{[t(`fleet.kind_${v.kind}` as MessageKey), t(`fleet.fuel_${v.fuel}` as MessageKey), v.color, v.location_name].filter(Boolean).join(' · ')}</span>
        </div>
        <StatusBadge tone={STATE[v.state].tone} icon={STATE[v.state].icon} label={t(`fleet.state_${v.state}` as MessageKey)} />
      </div>
      {live ? (
        <div className="fl-actions">
          {v.fuel !== 'electric' ? (
            <Button size="sm" variant="primary" icon="flame" onClick={() => setForm({ kind: 'fuel' })}>
              {t('fleet.add_fuel')}
            </Button>
          ) : null}
          <Button size="sm" icon="trendUp" onClick={() => setForm({ kind: 'odometer' })}>
            {t('fleet.add_reading')}
          </Button>
          <Button size="sm" icon="alert" onClick={() => setForm({ kind: 'issue' })}>
            {t('fleet.report_issue')}
          </Button>
          {manage ? (
            <>
              <span className="fl-actions-gap" />
              <Button size="sm" icon="contact" onClick={() => setForm({ kind: 'driver' })}>
                {t('fleet.change_driver')}
              </Button>
              <Button size="sm" icon="edit" onClick={() => setForm({ kind: 'edit' })}>
                {t('fleet.edit')}
              </Button>
            </>
          ) : null}
        </div>
      ) : (
        <p className="callout">
          <Icon name="archive" size={16} /> {t('fleet.retired_note', { reason: v.retired_reason ?? '' })}
        </p>
      )}
      <div className="segmented small fl-dtabs" role="tablist" aria-label={t('fleet.sections')}>
        {tabs
          .filter((x) => x.show)
          .map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setTab(x.key)}>
              <Icon name={x.icon} size={14} />
              {t(`fleet.dtab_${x.key}` as MessageKey)}
              {x.n ? <span className="count">{x.n}</span> : null}
            </button>
          ))}
      </div>

      {tab === 'about' ? (
        <div className="fl-about">
          <div className="item-summary">
            <div>
              <span className="muted small">{t('fleet.driver')}</span>
              <strong>{v.driver_name ?? t('fleet.no_driver')}</strong>
            </div>
            <div>
              <span className="muted small">{t('fleet.odometer')}</span>
              <strong>{km(v.odometer)}</strong>
            </div>
            <div>
              <span className="muted small">{t('fleet.consumption')}</span>
              <strong>{v.consumption !== null ? t('fleet.l_100', { n: v.consumption.toLocaleString(locale()) }) : '—'}</strong>
            </div>
            {cost12 !== null ? (
              <div>
                <span className="muted small">{t('fleet.cost_12')}</span>
                <strong>{money(cost12)}</strong>
              </div>
            ) : null}
          </div>
          {v.alerts.length ? <AlertList alerts={v.alerts} /> : v.can.view && live ? <p className="callout callout-ok">{t('fleet.all_in_order_long')}</p> : null}
          <dl className="fl-facts">
            {v.vin ? (
              <>
                <dt>{t('fleet.vin')}</dt>
                <dd className="mono">{v.vin}</dd>
              </>
            ) : null}
            {v.seats ? (
              <>
                <dt>{t('fleet.seats')}</dt>
                <dd>{v.seats}</dd>
              </>
            ) : null}
            {v.acquired ? (
              <>
                <dt>{t('fleet.acquired')}</dt>
                <dd>{dayText(v.acquired)}</dd>
              </>
            ) : null}
            {v.note ? (
              <>
                <dt>{t('fleet.note')}</dt>
                <dd className="pre-line">{v.note}</dd>
              </>
            ) : null}
          </dl>
          {manage ? (
            <div className="fl-state-tools">
              {v.state === 'active' ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon="settings"
                  busy={busy}
                  onClick={() => void run(async () => (await action('fleet.set_state', { id: v.id, state: 'workshop' }), await done()), t('fleet.to_workshop_done'))}
                >
                  {t('fleet.to_workshop')}
                </Button>
              ) : v.state === 'workshop' ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon="checkCircle"
                  busy={busy}
                  onClick={() => void run(async () => (await action('fleet.set_state', { id: v.id, state: 'active' }), await done()), t('fleet.back_in_use_done'))}
                >
                  {t('fleet.back_in_use')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  icon="undo"
                  busy={busy}
                  onClick={() => void run(async () => (await action('fleet.set_state', { id: v.id, state: 'active' }), await done()), t('fleet.back_in_use_done'))}
                >
                  {t('fleet.use_again')}
                </Button>
              )}
              {live ? (
                <Button size="sm" variant="ghost" icon="archive" onClick={() => setForm({ kind: 'retire' })}>
                  {t('fleet.retire')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : tab === 'fuel' ? (
        <TableWrap>
          <Table
            head={[t('fleet.day'), t('fleet.liters'), t('fleet.amount'), t('fleet.odometer'), t('fleet.station'), ...(manage ? [''] : [])]}
            phoneHide={[3, 4]}
            empty={t('fleet.no_fuel')}
            rows={v.fills.map((f) => ({
              key: f.id,
              cells: [
                dayText(f.day),
                `${f.liters.toLocaleString(locale())} l`,
                f.amount !== null ? money(f.amount) : '—',
                f.odometer !== null ? km(f.odometer) : '—',
                f.station ?? '',
                ...(manage ? [<IconButton key="x" icon="trash" label={t('fleet.remove')} onClick={() => setForm({ kind: 'drop-fuel', id: f.id })} />] : []),
              ],
            }))}
          />
        </TableWrap>
      ) : tab === 'services' ? (
        <div className="fl-list-tab">
          {manage && live ? (
            <div className="fl-tab-tools">
              <Button size="sm" icon="plus" onClick={() => setForm({ kind: 'service', service: null })}>
                {t('fleet.plan_service')}
              </Button>
            </div>
          ) : null}
          {v.services.length ? (
            <ul className="fl-services">
              {v.services.map((s) => (
                <li key={s.id} className={`fl-service ${s.state}`}>
                  <div className="fl-service-main">
                    <span className="fl-service-head">
                      <strong>{s.description}</strong>
                      <StatusBadge tone={S_STATE[s.state].tone} icon={S_STATE[s.state].icon} label={t(`fleet.sstate_${s.state}` as MessageKey)} />
                    </span>
                    <span className="muted small">
                      {[
                        t(`fleet.skind_${s.kind}` as MessageKey),
                        s.state === 'done' && s.day ? dayText(s.day) : null,
                        s.state === 'planned' && s.due_day ? t('fleet.due_day', { day: dayText(s.due_day) }) : null,
                        s.state === 'planned' && s.due_km !== null ? t('fleet.due_km', { km: km(s.due_km) }) : null,
                        s.odometer !== null ? km(s.odometer) : null,
                        s.vendor,
                        s.reported_by_name ? t('fleet.reported_by', { name: s.reported_by_name }) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                  {s.state === 'done' && s.amount !== null ? <strong className="fl-amount">{money(s.amount)}</strong> : null}
                  {manage && (s.state === 'reported' || s.state === 'planned') ? (
                    <span className="fl-service-acts">
                      <Button size="sm" variant="success" icon="check" onClick={() => setForm({ kind: 'finish', service: s })}>
                        {t('fleet.mark_done')}
                      </Button>
                      <IconButton icon="edit" label={s.state === 'reported' ? t('fleet.plan_it') : t('fleet.edit')} onClick={() => setForm({ kind: 'service', service: s })} />
                      <IconButton icon="x" label={t('fleet.cancel_service')} onClick={() => setForm({ kind: 'cancel-service', service: s })} />
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon="settings" title={t('fleet.no_services')} body={t('fleet.no_services_body')} />
          )}
        </div>
      ) : tab === 'papers' ? (
        <div className="fl-list-tab">
          {manage ? (
            <div className="fl-tab-tools">
              <Button size="sm" icon="plus" onClick={() => setForm({ kind: 'paper', paper: null })}>
                {t('fleet.add_paper')}
              </Button>
            </div>
          ) : null}
          <TableWrap>
            <Table
              head={[t('fleet.paper'), t('fleet.provider'), t('fleet.valid'), t('fleet.amount'), ...(manage ? [''] : [])]}
              phoneHide={[1, 3]}
              empty={t('fleet.no_papers')}
              rows={v.papers.map((p) => {
                const late = p.end && p.end < todayIso();
                return {
                  key: p.id,
                  cells: [
                    <span key="k" className="fl-paper-name">
                      <strong>{t(`fleet.paper_${p.kind}` as MessageKey)}</strong>
                      {p.reference ? <span className="muted small mono">{p.reference}</span> : null}
                    </span>,
                    p.provider ?? '',
                    <span key="v" className={late ? 'text-danger' : undefined}>
                      {[p.start ? dayText(p.start) : null, p.end ? dayText(p.end) : null].filter(Boolean).join(' – ') || '—'}
                    </span>,
                    money(p.amount),
                    ...(manage
                      ? [
                          <span key="a" className="fl-row-acts">
                            <IconButton icon="edit" label={t('fleet.edit')} onClick={() => setForm({ kind: 'paper', paper: p })} />
                            <IconButton icon="trash" label={t('fleet.remove')} onClick={() => setForm({ kind: 'drop-paper', paper: p })} />
                          </span>,
                        ]
                      : []),
                  ],
                };
              })}
            />
          </TableWrap>
        </div>
      ) : tab === 'drivers' ? (
        <div className="fl-list-tab">
          <DriverOn vehicle={v.id} />
          <TableWrap>
            <Table
              head={[t('fleet.driver'), t('fleet.from'), t('fleet.until')]}
              empty={t('fleet.no_drivers')}
              rows={v.drivers.map((d) => ({ key: d.id, cells: [d.name, dayText(d.start), d.end ? dayText(d.end) : t('fleet.still_driving')] }))}
            />
          </TableWrap>
        </div>
      ) : (
        <TableWrap>
          <Table
            head={[t('fleet.day'), t('fleet.odometer'), t('fleet.source'), ...(manage ? [''] : [])]}
            empty={t('fleet.no_readings')}
            rows={v.readings.map((r) => ({
              key: r.id,
              cells: [
                dayText(r.day),
                km(r.value),
                t(`fleet.src_${r.source}` as MessageKey),
                ...(manage
                  ? [
                      <IconButton
                        key="x"
                        icon="trash"
                        label={t('fleet.remove')}
                        onClick={() => void run(async () => (await action('fleet.remove_reading', { id: r.id }), await done()), t('fleet.reading_removed'))}
                      />,
                    ]
                  : []),
              ],
            }))}
          />
        </TableWrap>
      )}

      {form?.kind === 'edit' ? <VehicleForm initial={v} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'driver' ? <DriverDialog v={v} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'fuel' ? <FuelDialog v={v} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'odometer' ? <ReadingDialog v={v} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'issue' ? <IssueDialog v={v} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'service' ? <ServiceForm v={v} initial={form.service} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'finish' ? <FinishDialog v={v} service={form.service} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'paper' ? <PaperForm v={v} initial={form.paper} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'retire' ? <RetireDialog v={v} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'drop-paper' ? (
        <Confirm
          title={t('fleet.remove_paper_title')}
          body={t('fleet.remove_paper_body', { paper: t(`fleet.paper_${form.paper.kind}` as MessageKey) })}
          confirm={t('fleet.remove')}
          danger
          busy={busy}
          onClose={() => setForm(null)}
          onConfirm={() => void run(async () => (await action('fleet.remove_paper', { id: form.paper.id }), await done()), t('fleet.paper_removed'))}
        />
      ) : null}
      {form?.kind === 'drop-fuel' ? (
        <Confirm
          title={t('fleet.remove_fuel_title')}
          body={t('fleet.remove_fuel_body')}
          confirm={t('fleet.remove')}
          danger
          busy={busy}
          onClose={() => setForm(null)}
          onConfirm={() => void run(async () => (await action('fleet.remove_fuel', { id: form.id }), await done()), t('fleet.fuel_removed'))}
        />
      ) : null}
      {form?.kind === 'cancel-service' ? (
        <Confirm
          title={t('fleet.cancel_service_title')}
          body={t('fleet.cancel_service_body', { what: form.service.description })}
          confirm={t('fleet.cancel_service')}
          danger
          busy={busy}
          onClose={() => setForm(null)}
          onConfirm={() => void run(async () => (await action('fleet.cancel_service', { id: form.service.id }), await done()), t('fleet.service_cancelled'))}
        />
      ) : null}
    </Dialog>
  );
}

function DriverOn({ vehicle }: { vehicle: string }) {
  const [d, setD] = useState('');
  const [found, setFound] = useState<{ day: string; name: string | null } | null>(null);
  const { busy, run } = useSave();
  return (
    <div className="fl-driver-on">
      <Field label={t('fleet.who_drove')} hint={t('fleet.who_drove_hint')}>
        <div className="fl-inline">
          <input className="input" type="date" value={d} max={todayIso()} onChange={(e) => (setD(e.target.value), setFound(null))} />
          <Button
            size="sm"
            icon="search"
            busy={busy}
            disabled={!d}
            onClick={() => void run(async () => setFound(await action<{ day: string; name: string | null }>('fleet.driver_on', { vehicle, day: d })))}
          >
            {t('fleet.find')}
          </Button>
        </div>
      </Field>
      {found ? (
        <p className={`callout${found.name ? '' : ' callout-warn'}`} role="status">
          <Icon name={found.name ? 'contact' : 'alert'} size={16} />{' '}
          {found.name ? t('fleet.drove', { name: found.name, day: dayText(found.day) }) : t('fleet.nobody_drove', { day: dayText(found.day) })}
        </p>
      ) : null}
    </div>
  );
}

// ---- forms ------------------------------------------------------------------------------

function Foot({ busy, onClose, onSave, label, icon = 'check' }: { busy: boolean; onClose: () => void; onSave: () => void; label?: string; icon?: IconName }) {
  return (
    <span className="foot-actions">
      <Button variant="ghost" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button variant="primary" icon={icon} busy={busy} onClick={onSave}>
        {label ?? t('common.save')}
      </Button>
    </span>
  );
}
const int = (s: string) => (s.trim() === '' ? null : /^\d+$/.test(s.trim().replace(/[\s.,]/g, '')) ? Number(s.trim().replace(/[\s.,]/g, '')) : NaN);

function VehicleForm({ initial, onClose, onSaved }: { initial: Detail | null; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: ch } = useAction<{ locations: { id: string; name: string }[] }>('fleet.choices');
  const [f, setF] = useState({
    plate: initial?.plate ?? '',
    make: initial?.make ?? '',
    model: initial?.model ?? '',
    year: initial?.year ? String(initial.year) : '',
    kind: initial?.kind ?? ('van' as Kind),
    fuel: initial?.fuel ?? ('diesel' as Fuel),
    vin: initial?.vin ?? '',
    color: initial?.color ?? '',
    seats: initial?.seats ? String(initial.seats) : '',
    location: initial?.location ?? '',
    acquired: initial?.acquired ?? '',
    odometer: '',
    note: initial?.note ?? '',
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const year = int(f.year);
  const seats = int(f.seats);
  const odo = int(f.odometer);
  const errors = {
    plate: f.plate.trim().length < 2 ? t('fleet.e_plate') : null,
    make: !f.make.trim() ? t('fleet.e_make') : null,
    model: !f.model.trim() ? t('fleet.e_model') : null,
    year: year !== null && (Number.isNaN(year) || year < 1950 || year > 2100) ? t('fleet.e_year') : null,
    seats: seats !== null && (Number.isNaN(seats) || seats < 1 || seats > 99) ? t('fleet.e_seats') : null,
    odometer: odo !== null && Number.isNaN(odo) ? t('fleet.e_km') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(
      async () => {
        const v = await action<{ id: string }>('fleet.save_vehicle', {
          id: initial?.id ?? null,
          plate: f.plate.trim(),
          make: f.make.trim(),
          model: f.model.trim(),
          year,
          kind: f.kind,
          fuel: f.fuel,
          vin: f.vin.trim() || null,
          color: f.color.trim() || null,
          seats,
          location: f.location || null,
          acquired: f.acquired || null,
          note: f.note.trim() || null,
          ...(initial ? {} : { odometer: odo }),
        });
        await onSaved(v.id);
      },
      initial ? t('fleet.vehicle_saved') : t('fleet.vehicle_added', { plate: f.plate.trim().toUpperCase() }),
    );
  };
  return (
    <Dialog title={initial ? t('fleet.edit_vehicle') : t('fleet.add_vehicle')} onClose={onClose} size="lg" footer={<Foot busy={busy} onClose={onClose} onSave={save} />}>
      <div className="form-stack">
        <div className="fl-grid">
          <Field label={t('fleet.plate')} error={tried ? errors.plate : null} required>
            <input className="input mono fl-upper" value={f.plate} maxLength={20} onChange={(e) => set('plate', e.target.value)} data-autofocus autoComplete="off" placeholder="AA 123 BC" />
          </Field>
          <Field label={t('fleet.make')} error={tried ? errors.make : null} required>
            <input className="input" value={f.make} maxLength={60} onChange={(e) => set('make', e.target.value)} />
          </Field>
          <Field label={t('fleet.model')} error={tried ? errors.model : null} required>
            <input className="input" value={f.model} maxLength={60} onChange={(e) => set('model', e.target.value)} />
          </Field>
          <Field label={t('fleet.year')} error={tried ? errors.year : null}>
            <input className="input" inputMode="numeric" value={f.year} maxLength={4} onChange={(e) => set('year', e.target.value)} />
          </Field>
        </div>
        <Field label={t('fleet.kind')}>
          <div className="fl-pills" role="radiogroup" aria-label={t('fleet.kind')}>
            {KINDS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={f.kind === k} className={`fl-pill${f.kind === k ? ' on' : ''}`} onClick={() => set('kind', k)}>
                <Icon name={KIND_ICON[k]} size={14} /> {t(`fleet.kind_${k}` as MessageKey)}
              </button>
            ))}
          </div>
        </Field>
        <Field label={t('fleet.fuel')}>
          <div className="fl-pills" role="radiogroup" aria-label={t('fleet.fuel')}>
            {FUELS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={f.fuel === k} className={`fl-pill${f.fuel === k ? ' on' : ''}`} onClick={() => set('fuel', k)}>
                {t(`fleet.fuel_${k}` as MessageKey)}
              </button>
            ))}
          </div>
        </Field>
        <div className="fl-grid">
          <Field label={t('fleet.color')}>
            <input className="input" value={f.color} maxLength={40} onChange={(e) => set('color', e.target.value)} />
          </Field>
          <Field label={t('fleet.seats')} error={tried ? errors.seats : null}>
            <input className="input" inputMode="numeric" value={f.seats} maxLength={2} onChange={(e) => set('seats', e.target.value)} />
          </Field>
          <Field label={t('fleet.vin')} hint={t('fleet.vin_hint')}>
            <input className="input mono fl-upper" value={f.vin} maxLength={40} onChange={(e) => set('vin', e.target.value)} />
          </Field>
          <Field label={t('fleet.location')}>
            <select className="input" value={f.location} onChange={(e) => set('location', e.target.value)}>
              <option value="">{t('fleet.no_location')}</option>
              {ch?.locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('fleet.acquired')}>
            <input className="input" type="date" value={f.acquired} max={todayIso()} onChange={(e) => set('acquired', e.target.value)} />
          </Field>
          {!initial ? (
            <Field label={t('fleet.start_km')} error={tried ? errors.odometer : null} hint={t('fleet.start_km_hint')}>
              <input className="input" inputMode="numeric" value={f.odometer} onChange={(e) => set('odometer', e.target.value)} />
            </Field>
          ) : null}
        </div>
        <Field label={t('fleet.note')}>
          <textarea className="input" rows={2} maxLength={2000} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function DriverDialog({ v, onClose, onSaved }: { v: Detail; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: ch } = useAction<{ people: { id: string; name: string }[] }>('fleet.choices');
  const [driver, setDriver] = useState(v.driver ?? '');
  const [from, setFrom] = useState(todayIso());
  const save = () =>
    void run(
      async () => {
        await action('fleet.set_driver', { vehicle: v.id, driver: driver || null, from });
        await onSaved();
      },
      driver ? t('fleet.driver_set', { plate: v.plate, name: ch?.people.find((p) => p.id === driver)?.name ?? '' }) : t('fleet.driver_cleared', { plate: v.plate }),
    );
  return (
    <Dialog title={t('fleet.change_driver_of', { plate: v.plate })} onClose={onClose} size="sm" footer={<Foot busy={busy} onClose={onClose} onSave={save} />}>
      <div className="form-stack">
        <Field label={t('fleet.driver')}>
          <select className="input" value={driver} onChange={(e) => setDriver(e.target.value)} data-autofocus>
            <option value="">{t('fleet.no_driver')}</option>
            {ch?.people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('fleet.from_day')} hint={t('fleet.from_day_hint')}>
          <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function FuelDialog({ v, onClose, onSaved }: { v: Detail; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({ day: todayIso(), liters: '', amount: '', odometer: '', station: '', full: true });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, val: (typeof f)[K]) => setF((x) => ({ ...x, [k]: val }));
  const liters = Number(f.liters.replace(',', '.'));
  const amount = parseMoney(f.amount);
  const odo = int(f.odometer);
  const errors = {
    liters: !(liters > 0 && liters <= 2000) ? t('fleet.e_liters') : null,
    amount: amount === null ? t('fleet.e_amount') : null,
    odometer: odo !== null && (Number.isNaN(odo) || odo < v.odometer) ? t('fleet.e_km_low', { km: km(v.odometer) }) : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(
      async () => {
        await action('fleet.log_fuel', { vehicle: v.id, day: f.day, liters, amount, odometer: odo, full: f.full, station: f.station.trim() || null });
        await onSaved();
      },
      t('fleet.fuel_saved', { liters: liters.toLocaleString(locale()) }),
    );
  };
  return (
    <Dialog title={t('fleet.add_fuel_to', { plate: v.plate })} onClose={onClose} size="sm" footer={<Foot busy={busy} onClose={onClose} onSave={save} icon="flame" />}>
      <div className="form-stack">
        <div className="hr-two">
          <Field label={t('fleet.liters')} error={tried ? errors.liters : null} required>
            <input className="input" inputMode="decimal" value={f.liters} onChange={(e) => set('liters', e.target.value)} data-autofocus />
          </Field>
          <Field label={t('fleet.amount_paid')} error={tried ? errors.amount : null} required>
            <input className="input" inputMode="decimal" value={f.amount} onChange={(e) => set('amount', e.target.value)} />
          </Field>
        </div>
        <Field label={t('fleet.odometer_now')} error={tried ? errors.odometer : null} hint={t('fleet.odometer_now_hint', { km: km(v.odometer) })}>
          <input className="input" inputMode="numeric" value={f.odometer} onChange={(e) => set('odometer', e.target.value)} />
        </Field>
        <div className="hr-two">
          <Field label={t('fleet.day')}>
            <input className="input" type="date" value={f.day} max={todayIso()} onChange={(e) => set('day', e.target.value)} />
          </Field>
          <Field label={t('fleet.station')}>
            <input className="input" value={f.station} maxLength={80} onChange={(e) => set('station', e.target.value)} />
          </Field>
        </div>
        <div className="setting-row">
          <div>
            <strong>{t('fleet.full_tank')}</strong>
            <p className="muted">{t('fleet.full_tank_hint')}</p>
          </div>
          <Switch checked={f.full} onChange={(x) => set('full', x)} label={t('fleet.full_tank')} />
        </div>
      </div>
    </Dialog>
  );
}

function ReadingDialog({ v, onClose, onSaved }: { v: Detail; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [value, setValue] = useState('');
  const [d, setD] = useState(todayIso());
  const [tried, setTried] = useState(false);
  const n = int(value);
  const err = n === null || Number.isNaN(n) ? t('fleet.e_km') : d === todayIso() && n < v.odometer ? t('fleet.e_km_low', { km: km(v.odometer) }) : null;
  const save = () => {
    setTried(true);
    if (err) return;
    void run(
      async () => {
        await action('fleet.log_odometer', { vehicle: v.id, day: d, value: n });
        await onSaved();
      },
      t('fleet.reading_saved', { km: km(n!) }),
    );
  };
  return (
    <Dialog title={t('fleet.add_reading_to', { plate: v.plate })} onClose={onClose} size="sm" footer={<Foot busy={busy} onClose={onClose} onSave={save} />}>
      <div className="form-stack">
        <Field label={t('fleet.odometer_now')} error={tried ? err : null} hint={t('fleet.odometer_now_hint', { km: km(v.odometer) })} required>
          <input className="input" inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('fleet.day')}>
          <input className="input" type="date" value={d} max={todayIso()} onChange={(e) => setD(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function IssueDialog({ v, onClose, onSaved }: { v: Detail; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [text, setText] = useState('');
  const [tried, setTried] = useState(false);
  const save = () => {
    setTried(true);
    if (text.trim().length < 3) return;
    void run(async () => {
      await action('fleet.report_issue', { vehicle: v.id, description: text.trim() });
      await onSaved();
    }, t('fleet.issue_sent'));
  };
  return (
    <Dialog title={t('fleet.report_issue_on', { plate: v.plate })} onClose={onClose} size="sm" footer={<Foot busy={busy} onClose={onClose} onSave={save} label={t('fleet.send')} icon="send" />}>
      <div className="form-stack">
        <Field label={t('fleet.what_wrong')} hint={t('fleet.what_wrong_hint')} error={tried && text.trim().length < 3 ? t('fleet.e_what') : null} required>
          <textarea className="input" rows={4} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

function ServiceForm({ v, initial, onClose, onSaved }: { v: Detail; initial: Service | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    kind: initial?.kind ?? ('service' as SKind),
    description: initial?.description ?? '',
    due_day: initial?.due_day ?? '',
    due_km: initial?.due_km !== null && initial?.due_km !== undefined ? String(initial.due_km) : '',
    vendor: initial?.vendor ?? '',
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, val: (typeof f)[K]) => setF((x) => ({ ...x, [k]: val }));
  const dueKm = int(f.due_km);
  const errors = { description: f.description.trim().length < 3 ? t('fleet.e_service') : null, due_km: dueKm !== null && Number.isNaN(dueKm) ? t('fleet.e_km') : null };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('fleet.save_service', {
        id: initial?.id ?? null,
        vehicle: v.id,
        kind: f.kind,
        description: f.description.trim(),
        due_day: f.due_day || null,
        due_km: dueKm,
        vendor: f.vendor.trim() || null,
      });
      await onSaved();
    }, t('fleet.service_saved'));
  };
  return (
    <Dialog
      title={initial?.state === 'reported' ? t('fleet.plan_reported') : initial ? t('fleet.edit_service') : t('fleet.plan_service_for', { plate: v.plate })}
      onClose={onClose}
      size="md"
      footer={<Foot busy={busy} onClose={onClose} onSave={save} />}
    >
      <div className="form-stack">
        <Field label={t('fleet.service_kind')}>
          <div className="fl-pills" role="radiogroup" aria-label={t('fleet.service_kind')}>
            {S_KINDS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={f.kind === k} className={`fl-pill${f.kind === k ? ' on' : ''}`} onClick={() => set('kind', k)}>
                {t(`fleet.skind_${k}` as MessageKey)}
              </button>
            ))}
          </div>
        </Field>
        <Field label={t('fleet.what_to_do')} error={tried ? errors.description : null} required>
          <input className="input" value={f.description} maxLength={2000} onChange={(e) => set('description', e.target.value)} data-autofocus />
        </Field>
        <div className="hr-two">
          <Field label={t('fleet.due_by_day')}>
            <input className="input" type="date" value={f.due_day} onChange={(e) => set('due_day', e.target.value)} />
          </Field>
          <Field label={t('fleet.due_by_km')} error={tried ? errors.due_km : null} hint={t('fleet.now_at', { km: km(v.odometer) })}>
            <input className="input" inputMode="numeric" value={f.due_km} onChange={(e) => set('due_km', e.target.value)} />
          </Field>
        </div>
        <Field label={t('fleet.vendor')}>
          <input className="input" value={f.vendor} maxLength={80} onChange={(e) => set('vendor', e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function FinishDialog({ v, service, onClose, onSaved }: { v: Detail; service: Service; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({ day: todayIso(), odometer: '', amount: '', vendor: service.vendor ?? '', next_day: '', next_km: '' });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, val: (typeof f)[K]) => setF((x) => ({ ...x, [k]: val }));
  const amount = parseMoney(f.amount);
  const odo = int(f.odometer);
  const nextKm = int(f.next_km);
  const errors = {
    amount: amount === null ? t('fleet.e_amount') : null,
    odometer: odo !== null && Number.isNaN(odo) ? t('fleet.e_km') : null,
    next_km: nextKm !== null && Number.isNaN(nextKm) ? t('fleet.e_km') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('fleet.finish_service', { id: service.id, day: f.day, odometer: odo, amount, vendor: f.vendor.trim() || null, next_day: f.next_day || null, next_km: nextKm });
      await onSaved();
    }, t('fleet.service_done'));
  };
  return (
    <Dialog title={t('fleet.finish_title', { what: service.description })} onClose={onClose} size="md" footer={<Foot busy={busy} onClose={onClose} onSave={save} label={t('fleet.mark_done')} />}>
      <div className="form-stack">
        <div className="hr-two">
          <Field label={t('fleet.done_on')}>
            <input className="input" type="date" value={f.day} max={todayIso()} onChange={(e) => set('day', e.target.value)} />
          </Field>
          <Field label={t('fleet.cost')} error={tried ? errors.amount : null} hint={t('fleet.cost_hint')} required>
            <input className="input" inputMode="decimal" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-autofocus />
          </Field>
        </div>
        <div className="hr-two">
          <Field label={t('fleet.odometer_then')} error={tried ? errors.odometer : null} hint={t('fleet.now_at', { km: km(v.odometer) })}>
            <input className="input" inputMode="numeric" value={f.odometer} onChange={(e) => set('odometer', e.target.value)} />
          </Field>
          <Field label={t('fleet.vendor')}>
            <input className="input" value={f.vendor} maxLength={80} onChange={(e) => set('vendor', e.target.value)} />
          </Field>
        </div>
        <fieldset className="fl-next">
          <legend>{t('fleet.next_one')}</legend>
          <div className="hr-two">
            <Field label={t('fleet.due_by_day')}>
              <input className="input" type="date" value={f.next_day} onChange={(e) => set('next_day', e.target.value)} />
            </Field>
            <Field label={t('fleet.due_by_km')} error={tried ? errors.next_km : null}>
              <input className="input" inputMode="numeric" value={f.next_km} onChange={(e) => set('next_km', e.target.value)} />
            </Field>
          </div>
          <p className="muted small">{t('fleet.next_one_hint')}</p>
        </fieldset>
      </div>
    </Dialog>
  );
}

function PaperForm({ v, initial, onClose, onSaved }: { v: Detail; initial: Paper | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    kind: initial?.kind ?? ('insurance' as PKind),
    reference: initial?.reference ?? '',
    provider: initial?.provider ?? '',
    start: initial?.start ?? todayIso(),
    end: initial?.end ?? '',
    amount: initial ? plain(initial.amount) : '',
    note: initial?.note ?? '',
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, val: (typeof f)[K]) => setF((x) => ({ ...x, [k]: val }));
  const amount = f.amount.trim() ? parseMoney(f.amount) : 0;
  const errors = { amount: amount === null ? t('fleet.e_amount') : null, end: f.start && f.end && f.end < f.start ? t('fleet.e_end') : null };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('fleet.save_paper', {
        id: initial?.id ?? null,
        vehicle: v.id,
        kind: f.kind,
        reference: f.reference.trim() || null,
        provider: f.provider.trim() || null,
        start: f.start || null,
        end: f.end || null,
        amount,
        note: f.note.trim() || null,
      });
      await onSaved();
    }, t('fleet.paper_saved'));
  };
  return (
    <Dialog title={initial ? t('fleet.edit_paper') : t('fleet.add_paper_for', { plate: v.plate })} onClose={onClose} size="md" footer={<Foot busy={busy} onClose={onClose} onSave={save} />}>
      <div className="form-stack">
        <Field label={t('fleet.paper')}>
          <div className="fl-pills" role="radiogroup" aria-label={t('fleet.paper')}>
            {P_KINDS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={f.kind === k} className={`fl-pill${f.kind === k ? ' on' : ''}`} onClick={() => set('kind', k)}>
                {t(`fleet.paper_${k}` as MessageKey)}
              </button>
            ))}
          </div>
        </Field>
        <div className="hr-two">
          <Field label={t('fleet.provider')}>
            <input className="input" value={f.provider} maxLength={80} onChange={(e) => set('provider', e.target.value)} />
          </Field>
          <Field label={t('fleet.reference')}>
            <input className="input mono" value={f.reference} maxLength={80} onChange={(e) => set('reference', e.target.value)} />
          </Field>
          <Field label={t('fleet.start')}>
            <input className="input" type="date" value={f.start} onChange={(e) => set('start', e.target.value)} />
          </Field>
          <Field label={t('fleet.end')} error={tried ? errors.end : null} hint={t('fleet.end_hint')}>
            <input className="input" type="date" value={f.end} onChange={(e) => set('end', e.target.value)} />
          </Field>
        </div>
        <Field label={t('fleet.amount')} error={tried ? errors.amount : null} hint={t('fleet.paper_amount_hint')}>
          <input className="input" inputMode="decimal" value={f.amount} onChange={(e) => set('amount', e.target.value)} />
        </Field>
        <Field label={t('fleet.note')}>
          <textarea className="input" rows={2} maxLength={1000} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function RetireDialog({ v, onClose, onSaved }: { v: Detail; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const save = () => {
    setTried(true);
    if (!reason.trim()) return;
    void run(
      async () => {
        await action('fleet.set_state', { id: v.id, state: 'retired', reason: reason.trim() });
        await onSaved();
      },
      t('fleet.retired_done', { plate: v.plate }),
    );
  };
  return (
    <Dialog
      title={t('fleet.retire_title', { plate: v.plate })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" icon="archive" busy={busy} onClick={save}>
            {t('fleet.retire')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('fleet.retire_body')}</p>
        <Field label={t('fleet.retire_reason')} error={tried && !reason.trim() ? t('fleet.e_reason') : null} required>
          <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} data-autofocus placeholder={t('fleet.retire_placeholder')} />
        </Field>
      </div>
    </Dialog>
  );
}
